import { alpha, useTheme } from '@mui/material/styles'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'
import { useChat } from '../context/useChat'

const TYPING_IDLE_MS = 2000

const resizeTextarea = (textarea?: HTMLTextAreaElement | null) => {
  if (!textarea) return
  textarea.style.height = 'auto'
  textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`
}

export const MessageInput = () => {
  const theme = useTheme()
  const { sendMessage, beginTyping, endTyping, loading, connectionState } =
    useChat()
  const [value, setValue] = useState('')
  const [sending, setSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const clearTypingTimer = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current)
      typingTimerRef.current = undefined
    }
  }, [])

  useEffect(() => {
    resizeTextarea(textareaRef.current)
  }, [])

  useEffect(() => {
    return () => {
      clearTypingTimer()
      endTyping()
    }
  }, [clearTypingTimer, endTyping])

  const scheduleTypingEnd = useCallback(() => {
    clearTypingTimer()
    typingTimerRef.current = setTimeout(() => {
      endTyping()
    }, TYPING_IDLE_MS)
  }, [clearTypingTimer, endTyping])

  const isDisabled =
    loading ||
    sending ||
    connectionState === 'disconnected' ||
    connectionState === 'disconnecting'

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (isDisabled) return
    const nextValue = event.target.value
    setValue(nextValue)
    resizeTextarea(event.target)
    if (nextValue.trim().length > 0) {
      beginTyping()
      scheduleTypingEnd()
      return
    }
    clearTypingTimer()
    endTyping()
  }

  const handleSend = useCallback(async () => {
    const trimmed = value.trim()
    if (!trimmed) return

    setSending(true)
    try {
      await sendMessage(trimmed)
      setValue('')
      resizeTextarea(textareaRef.current)
    } finally {
      setSending(false)
      clearTypingTimer()
      endTyping()
    }
  }, [clearTypingTimer, endTyping, sendMessage, value])

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const canSend = value.trim().length > 0 && !isDisabled

  return (
    <div className="flex items-end gap-3">
      <div
        className="flex flex-1 items-end rounded-2xl border bg-slate-50/60 px-4 py-2.5 transition-colors focus-within:border-slate-300 focus-within:bg-white"
        style={{ borderColor: alpha(theme.palette.divider, 0.6) }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder="Type a message..."
          rows={1}
          className="max-h-[160px] min-h-[24px] flex-1 resize-none bg-transparent text-sm leading-relaxed text-slate-800 outline-none placeholder:text-slate-400"
          aria-label="Message input"
        />
      </div>

      <button
        type="button"
        onClick={handleSend}
        disabled={!canSend}
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white shadow-md transition-all disabled:scale-95 disabled:opacity-40 disabled:shadow-none"
        style={{
          background: canSend
            ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
            : theme.palette.grey[300],
        }}
        aria-label="Send message"
      >
        <svg
          className="h-[18px] w-[18px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 2 11 13" />
          <path d="M22 2 15 22 11 13 2 9l20-7Z" />
        </svg>
      </button>
    </div>
  )
}
